import { useEffect, useState } from 'react';
import { History, Globe, LockKeyhole, Loader2, ShieldCheck } from 'lucide-react';
import { api, dateLabel, money } from '../lib/api';
import { Badge, EmptyState, SectionHeading } from './ui';

const sources = {
  Scores: 'scores',
  Matches: 'match-history',
  Permissions: 'consent-history',
  Website: 'website-events',
} as const;
type HistoryKind = keyof typeof sources;
const stamp = (value: string) =>
  `${dateLabel(value)} · ${new Date(value).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata' })}`;

export function ContactEvidence({ contactId, version }: { contactId: string; version: number }) {
  const [kind, setKind] = useState<HistoryKind>('Scores');
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError('');
    api(`/contacts/${contactId}/${sources[kind]}`)
      .then((result) => {
        if (alive) setRows(Array.isArray(result) ? result : result.data || []);
      })
      .catch((e) => {
        if (alive) setError(e.message);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [contactId, version, kind]);
  return (
    <section className="evidence-history">
      <SectionHeading
        title="The evidence behind the relationship"
        subtitle="Dated decisions, original sources, and a history you can trace."
      />
      <label className="field">
        <span>Evidence history</span>
        <select value={kind} onChange={(e) => setKind(e.target.value as HistoryKind)}>
          {Object.keys(sources).map((name) => (
            <option key={name}>{name}</option>
          ))}
        </select>
      </label>
      {loading ? (
        <div className="history-loading">
          <Loader2 className="spin" size={20} /> Loading recorded evidence…
        </div>
      ) : error ? (
        <EmptyState icon={LockKeyhole} title="This history is unavailable" description={error} />
      ) : !rows.length ? (
        <EmptyState
          icon={History}
          title="A clear beginning"
          description={`No ${kind.toLowerCase()} history has been recorded for this relationship yet.`}
        />
      ) : (
        rows.map((row) => (
          <article className="history-record" key={row.id || row.eventId}>
            <div className="history-record-top">
              <span>
                <History size={13} />
                {stamp(row.createdAt || row.occurredAt)}
              </span>
              <Badge>
                {kind === 'Scores' || kind === 'Matches'
                  ? row.modelVersion
                  : kind === 'Permissions'
                    ? row.channel
                    : 'Website'}
              </Badge>
            </div>
            {kind === 'Scores' && (
              <>
                <div className="history-score">
                  <strong>
                    {Math.round(Number(row.score))}
                    <small>Model score</small>
                  </strong>
                  <span>
                    Relationship version {row.evidence?.contactVersion || '—'}
                    <br />
                    {Math.round(Number(row.confidence) * 100)}% evidence confidence
                  </span>
                </div>
                {row.evidence?.override && (
                  <p className="history-context">
                    Advisor score {row.evidence.effectiveScore} until{' '}
                    {dateLabel(row.evidence.override.expiresAt)}
                    {row.evidence.override.reason ? ` · ${row.evidence.override.reason}` : ''}
                  </p>
                )}
                <div className="history-components">
                  {Object.entries(row.components || {}).map(([key, value]) => (
                    <span key={key}>
                      {key === 'capacity' ? 'Capacity band' : key}
                      <b>{Math.round(Number(value))}</b>
                    </span>
                  ))}
                </div>
              </>
            )}
            {kind === 'Matches' && (
              <>
                <p className="history-context">
                  {row.matches?.length || 0} candidates · Requirement version {row.contactVersion} ·{' '}
                  {row.manualVerify ? 'Manual verification mode' : 'Verified availability mode'}
                </p>
                {row.invalidations?.length > 0 && (
                  <div className="match-warning">
                    {row.invalidations.length} inventory changes since this recommendation. Open
                    current matches before advising a buyer.
                  </div>
                )}
                {(row.matches || []).slice(0, 5).map((match: any) => (
                  <div className="history-match" key={match.propertyId}>
                    <div>
                      <strong>{match.property?.name || 'Property'}</strong>
                      <span>
                        {match.property?.locality} · {money(match.property?.price || 0)}
                      </span>
                    </div>
                    <Badge tone={match.eligible === false ? 'amber' : 'green'}>
                      {match.eligible === false ? 'Recheck' : `${Math.round(match.score)}% fit`}
                    </Badge>
                  </div>
                ))}
                <details>
                  <summary>Decision context</summary>
                  <p>
                    Availability window: {row.staleDays} days. This snapshot preserves the inventory
                    and requirement versions evaluated at the time.
                  </p>
                  {row.invalidations?.map((item: any, i: number) => (
                    <p key={i}>
                      {dateLabel(item.createdAt)} · {item.reason.replaceAll('_', ' ')}
                    </p>
                  ))}
                </details>
              </>
            )}
            {kind === 'Permissions' && (
              <>
                <h3>
                  <ShieldCheck size={16} />
                  {row.allowed ? 'Permission granted' : 'Permission withdrawn'}
                </h3>
                <p className="history-context">
                  {(row.purposes || [])
                    .map((purpose: string) =>
                      purpose === 'advertising_measurement'
                        ? 'Ad conversion measurement'
                        : purpose === 'advertising'
                          ? 'Advertising audience'
                          : purpose.replaceAll('_', ' '),
                    )
                    .join(' · ')}
                </p>
                {row.source && (
                  <dl>
                    <dt>Source</dt>
                    <dd>{row.source}</dd>
                    <dt>Evidence</dt>
                    <dd>{row.evidence}</dd>
                    <dt>Reason</dt>
                    <dd>{row.reason}</dd>
                    <dt>Recorded by</dt>
                    <dd>{row.actor}</dd>
                  </dl>
                )}
              </>
            )}
            {kind === 'Website' && (
              <>
                <h3>
                  <Globe size={16} />
                  {String(row.eventName).replaceAll('_', ' ')}
                </h3>
                <p className="history-context">
                  {row.payload?.page?.path || 'Consented website interaction'}
                </p>
                <div className="history-components">
                  {Object.entries(row.payload?.marketing || {})
                    .filter(([, value]) => value)
                    .map(([key, value]) => (
                      <span key={key}>
                        {key.replaceAll('utm_', '').replaceAll('_', ' ')}
                        <b>{String(value)}</b>
                      </span>
                    ))}
                </div>
                <small>
                  Linked through a verified identity. Original anonymous event retained.
                </small>
              </>
            )}
          </article>
        ))
      )}
    </section>
  );
}
